/** Propostas comerciais — integração com a API. */

import apiClient from './apiClient';
import type {
  ResumoDePropostas,
  AberturaDaProposta,
  DadosDaProposta,
  Proposta,
  PropostaPublica,
  TotaisDaProposta,
} from '../types/proposal';

export * from '../types/proposal';

export const proposalService = {
  async listar(): Promise<Proposta[]> {
    const { data } = await apiClient.get<Proposta[]>('/propostas');
    return data ?? [];
  },

  /** Quanto a carteira produziu no período, por situação. Ver GET /propostas/resumo. */
  async resumo(periodo?: { inicio?: string; fim?: string }): Promise<ResumoDePropostas> {
    const params = new URLSearchParams();
    if (periodo?.inicio) params.set('inicio', periodo.inicio);
    if (periodo?.fim) params.set('fim', periodo.fim);
    const query = params.toString();
    const { data } = await apiClient.get<ResumoDePropostas>(
      `/propostas/resumo${query ? `?${query}` : ''}`
    );
    if (!data) throw new Error('Resposta vazia do servidor ao montar o resumo de propostas');
    return data;
  },

  async porId(id: number): Promise<Proposta> {
    const { data } = await apiClient.get<Proposta>(`/propostas/${id}`);
    if (!data) throw new Error('Proposta não encontrada');
    return data;
  },

  async criar(dados: DadosDaProposta): Promise<Proposta> {
    const { data } = await apiClient.post<Proposta>('/propostas', dados);
    if (!data) throw new Error('Resposta vazia do servidor ao criar a proposta');
    return data;
  },

  async atualizar(id: number, dados: DadosDaProposta): Promise<Proposta> {
    const { data } = await apiClient.put<Proposta>(`/propostas/${id}`, dados);
    if (!data) throw new Error('Resposta vazia do servidor ao salvar a proposta');
    return data;
  },

  async remover(id: number): Promise<void> {
    await apiClient.delete(`/propostas/${id}`);
  },

  /** Gera (ou devolve) o link do cliente. Clicar de novo não invalida o que já foi enviado. */
  async gerarLink(id: number): Promise<string> {
    const { data } = await apiClient.post<{ shareToken: string }>(`/propostas/${id}/link`, {});
    if (!data?.shareToken) throw new Error('O servidor não devolveu o link');
    return data.shareToken;
  },

  async aberturas(id: number): Promise<AberturaDaProposta[]> {
    const { data } = await apiClient.get<AberturaDaProposta[]>(`/propostas/${id}/aberturas`);
    return data ?? [];
  },

  /** A proposta pelo link público. Não exige sessão — quem abre é o cliente. */
  async publica(token: string): Promise<PropostaPublica> {
    const { data } = await apiClient.get<PropostaPublica>(`/propostas/publica/${token}`);
    if (!data) throw new Error('Proposta não encontrada ou link expirado');
    return data;
  },

  /** Confere no servidor o total que a tela calculou. Usado ao salvar, não a cada tecla. */
  async previa(dados: DadosDaProposta): Promise<TotaisDaProposta> {
    const { data } = await apiClient.post<TotaisDaProposta>('/propostas/previa', dados);
    if (!data) throw new Error('Resposta vazia do servidor ao calcular os totais');
    return data;
  },
};

export default proposalService;
